import { useEffect, useMemo, useRef, useState } from "react";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { monsterIcon, ITEM_ICON, itemName, SKILL_LABEL, skillIcon } from "@/lib/gameData";
import {
  Swords, StopCircle, Sparkles, Crosshair, ScrollText, Wand2,
  Shield, Heart, Flame, Target, ChevronDown, Skull, Coins,
} from "lucide-react";

const COMBAT_SKILLS = ["attack", "strength", "defence", "hitpoints", "ranged", "magic", "prayer"];

// -- Small primitives (mirror ProfilePanel Card/StatRow aesthetic) ---------

function Card({ title, right, children, className = "", testid }) {
  return (
    <section className={"panel p-3 " + className} data-testid={testid}>
      {title && (
        <header className="flex items-center justify-between mb-2">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">{title}</h3>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

function SkillBadge({ skill, lv, focused, forwardedRef }) {
  const url = skillIcon(skill);
  return (
    <div
      ref={forwardedRef}
      data-testid={`combat-skill-${skill}`}
      title={`${SKILL_LABEL[skill]} Lv ${lv}`}
      className={
        "panel-inset p-1.5 flex flex-col items-center gap-0.5 transition-colors " +
        (focused ? "focus-flash" : "")
      }
    >
      <div className="text-[9px] font-numeric text-stone-500 uppercase tracking-wider">Lv. {lv}</div>
      {url ? (
        <img src={url} alt={SKILL_LABEL[skill]} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center text-[9px] font-numeric text-amber-400">
          {SKILL_LABEL[skill]?.slice(0, 3)}
        </div>
      )}
      <div className="text-[9px] font-body text-stone-400 leading-none">{SKILL_LABEL[skill]}</div>
    </div>
  );
}

function StatRow({ label, level, value, max = 99 }) {
  const pct = Math.min(100, Math.round(((level ?? 0) / max) * 100));
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="w-32 flex items-center justify-between text-sm">
        <span className="text-stone-200 font-body">{label}</span>
        {level != null && (
          <span className="text-[10px] font-numeric text-stone-500 bg-stone-900 border border-stone-700 rounded px-1.5 py-0.5">
            Lv. {level}
          </span>
        )}
      </div>
      <div className="flex-1 xp-bar-outer" style={{ height: 6 }}>
        <div className="xp-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      {value != null && (
        <span className="w-14 text-right text-xs font-numeric text-amber-300">{value}</span>
      )}
    </div>
  );
}

function StyleButton({ active, onClick, disabled, icon: Icon, label, hint, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={hint}
      className={
        "flex-1 flex items-center gap-2 px-2 py-1.5 rounded border text-xs font-body transition-colors duration-200 " +
        (active
          ? "bg-amber-800/50 border-amber-500 text-amber-100"
          : disabled
            ? "bg-stone-900/50 border-stone-800 text-stone-600 cursor-not-allowed"
            : "bg-stone-900 border-stone-700 text-stone-300 hover:border-amber-700 hover:text-amber-200")
      }
    >
      <Icon size={14} strokeWidth={1.6} />
      <span className="leading-tight">{label}</span>
    </button>
  );
}

// -- Derived combat stats (shape mirrors ProfilePanel derivedStats) --------

function derivedCombatStats(snap) {
  const s = snap?.skills || {};
  const atk = s.attack?.level ?? 1;
  const str = s.strength?.level ?? 1;
  const def = s.defence?.level ?? 1;
  const rng = s.ranged?.level ?? 1;
  const mag = s.magic?.level ?? 1;
  const pray = s.prayer?.level ?? 1;
  const hp = s.hitpoints?.level ?? 10;
  return [
    { key: "max_hit_melee",  label: "Max Hit (Melee)",  desc: "Cap of melee damage per strike.",
      value: `${Math.max(1, Math.floor((str + 8) / 4))}`, icon: Swords },
    { key: "max_hit_ranged", label: "Max Hit (Ranged)", desc: "Cap of ranged damage per strike.",
      value: `${Math.max(1, Math.floor((rng + 6) / 4))}`, icon: Crosshair },
    { key: "max_hit_magic",  label: "Max Hit (Magic)",  desc: "Cap of magic damage per cast.",
      value: `${Math.max(1, Math.floor((mag + 4) / 4))}`, icon: Wand2 },
    { key: "accuracy",       label: "Accuracy",         desc: "Chance to land a successful hit.",
      value: `${Math.min(96, 55 + atk)}%`, icon: Target },
    { key: "protection",     label: "Protection",       desc: "Damage reduction from defence.",
      value: `${def + 5}`, icon: Shield },
    { key: "prayer_pool",    label: "Prayer Pool",      desc: "Total prayer points available.",
      value: `${pray}`, icon: Sparkles },
    { key: "hp_regen",       label: "HP Regen (per tick)", desc: "Hitpoints restored between fights.",
      value: `+${Math.max(1, Math.floor(hp / 10))}`, icon: Heart },
    { key: "combat_level",   label: "Combat Level",     desc: "Aggregated tier of your combat power.",
      value: `${Math.floor((atk + str + def + Math.floor(hp/2)) / 4)}`, icon: Flame },
  ];
}

// -- Slayer widget (compact card version) ---------------------------------

function SlayerCard() {
  const { snap, content, newSlayerTask } = useGame();
  const task = snap?.slayer_task;
  const monsters = content?.monsters || {};
  const master = content?.task_master;

  async function requestTask() {
    try { await newSlayerTask(); toast.success("New task assigned!"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  const monster = task && monsters[task.monster_id];
  const progressPct = task ? Math.round(((task.initial - task.remaining) / task.initial) * 100) : 0;

  return (
    <Card title="Slayer Task" testid="slayer-widget" right={<ScrollText size={12} className="text-amber-500" />}>
      {task && monster ? (
        <div data-testid="slayer-task" className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded panel-inset overflow-hidden">
              {monsterIcon(task.monster_id) ? (
                <img src={monsterIcon(task.monster_id)} alt={monster.name} className="w-full h-full object-cover" />
              ) : (
                <Swords size={16} className="text-red-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm leading-tight truncate">Slay {monster.name}</div>
              <div className="font-numeric text-[10px] text-stone-500">
                {task.initial - task.remaining} / {task.initial} killed
              </div>
            </div>
          </div>
          <div className="xp-bar-outer">
            <div className="xp-bar-fill" style={{ width: `${progressPct}%` }} data-testid="slayer-progress-fill" />
          </div>
          <div className="text-[10px] text-amber-300 font-numeric">
            Reward: +{task.bonus_xp} Slayer XP
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-stone-500 italic font-body leading-snug">
            {master?.name || "The Task-Master"} awaits.
          </p>
          <button onClick={requestTask} className="btn-gold w-full py-1.5 text-xs" data-testid="get-task-btn">
            Ask for a task
          </button>
        </div>
      )}
    </Card>
  );
}

// -- Combat style + spellbook (compact) -----------------------------------

function StyleAndSpells() {
  const { snap, content, setCombatStyle, setActiveSpell } = useGame();
  const spells = content?.spells || {};
  const magicLv = snap?.skills?.magic?.level ?? 1;
  const equippedId = snap?.equipment?.weapon;
  const equipped = equippedId ? content?.items?.[equippedId] : null;
  const canRanged = equipped?.style === "ranged";
  const style = snap?.combat_style || "melee";
  const activeSpellId = snap?.active_spell;

  async function chooseStyle(s) {
    try { await setCombatStyle(s); } catch (e) { toast.error(formatApiError(e)); }
  }
  async function chooseSpell(id) {
    try { await setActiveSpell(id); } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <Card
      title="Combat Style"
      testid="combat-style-widget"
      right={
        <span className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
          Now · <span className="text-amber-400">{style}</span>
        </span>
      }
    >
      <div className="flex gap-1.5 mb-2">
        <StyleButton active={style === "melee"} onClick={() => chooseStyle("melee")}
          icon={Swords}     label="Melee"   hint="Fists / swords" testid="style-melee" />
        <StyleButton active={style === "ranged"} onClick={() => chooseStyle("ranged")}
          disabled={!canRanged} icon={Crosshair} label="Ranged" hint={canRanged ? "Bow equipped" : "Equip a bow"} testid="style-ranged" />
        <StyleButton active={style === "magic"} onClick={() => chooseStyle("magic")}
          icon={Wand2}      label="Magic"   hint="Pick a spell"   testid="style-magic" />
      </div>
      {style === "magic" && (
        <div className="space-y-1" data-testid="spell-picker">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-numeric">Spell book</div>
          {Object.entries(spells).map(([id, spell]) => {
            const locked = magicLv < spell.level;
            const active = activeSpellId === id;
            return (
              <button
                key={id}
                onClick={() => chooseSpell(id)}
                disabled={locked}
                data-testid={`spell-${id}`}
                className={
                  "w-full flex items-center justify-between px-2 py-1 rounded border text-xs font-body transition-colors duration-200 " +
                  (active
                    ? "bg-purple-900/40 border-purple-500 text-purple-100"
                    : locked
                      ? "bg-stone-900/60 border-stone-800 text-stone-600 cursor-not-allowed"
                      : "bg-stone-900 border-stone-700 text-stone-300 hover:border-purple-700 hover:text-purple-200")
                }
              >
                <span className="flex items-center gap-1.5 min-w-0 truncate">
                  <Sparkles size={11} /> {spell.name}
                </span>
                <span className="font-numeric text-[10px] opacity-75 shrink-0">
                  L{spell.level} · {spell.max_hit}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// -- Main panel -----------------------------------------------------------

export function CombatPanel({ focusSkill, focusKey }) {
  const { snap, content, startCombat, stopAction, unequip } = useGame();
  const monsters = content?.monsters || {};
  const items = content?.items || {};
  const combat = snap?.combat;
  const activeMonster = combat && monsters[combat.monster_id];
  const [showSecondary, setShowSecondary] = useState(true);
  const badgeRefs = useRef({});

  const monsterList = useMemo(() => Object.entries(monsters).map(([id, m]) => ({ id, ...m })), [monsters]);

  const equippedId = snap?.equipment?.weapon;
  const equipped = equippedId ? items[equippedId] : null;

  async function fight(id) {
    try { await startCombat(id); toast.success(`Engaging ${monsters[id].name}!`); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function flee() {
    try { await stopAction(); toast.message("Withdrew from combat."); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function doUnequip() {
    try { await unequip(); toast.message("Unequipped weapon."); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  // Deep-link: scroll to & flash a combat skill badge when routed via LeftDock.
  useEffect(() => {
    if (!focusSkill) return;
    const el = badgeRefs.current[focusSkill];
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("focus-flash");
      const clear = setTimeout(() => el.classList.remove("focus-flash"), 1900);
      el.__flashClear = clear;
    }, 40);
    return () => {
      clearTimeout(t);
      if (el.__flashClear) clearTimeout(el.__flashClear);
      el.classList.remove("focus-flash");
    };
  }, [focusSkill, focusKey]);

  const monsterHpPct = activeMonster ? Math.max(0, (combat.monster_hp / activeMonster.hp) * 100) : 0;
  const heroImg = "/generated/hero_landing.png";
  const secondary = derivedCombatStats(snap);
  const totalCombatLv = COMBAT_SKILLS.reduce((s, k) => s + (snap?.skills?.[k]?.level ?? 0), 0);

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="combat-panel">
      {/* Tabs (mirror Profile) */}
      <div className="flex items-center gap-2 text-xs font-body px-1">
        <button className="text-amber-400 border-b-2 border-amber-500 pb-1 px-1" data-testid="tab-battle">Battle</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">Dungeons</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">World Bosses</button>
      </div>

      {/* Portrait band */}
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.4), rgba(12,10,9,0.92)), url(${heroImg})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-24 h-24 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <Swords size={40} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl heading-gilded">The Arena</h1>
            <div className="flex flex-wrap gap-4 mt-1 text-sm font-numeric text-stone-300">
              <span>Combat Total · <span className="text-amber-300">{totalCombatLv}</span></span>
              <span>Style · <span className="text-amber-300">{snap?.combat_style || "melee"}</span></span>
              <span>Status · <span className={activeMonster ? "text-red-300" : "text-emerald-300"}>{activeMonster ? "In combat" : "Idle"}</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Left column: Style + Slayer + Equipped */}
        <div className="lg:col-span-3 space-y-3">
          <StyleAndSpells />
          <SlayerCard />

          <Card title="Equipped Weapon" testid="equipped-card">
            {equipped ? (
              <div className="flex items-center gap-3" data-testid="equipped-weapon">
                <div className="w-14 h-14 inv-slot text-3xl">
                  {ITEM_ICON[equippedId] || "⚔"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base leading-tight truncate">{equipped.name}</div>
                  <div className="font-numeric text-[10px] text-amber-500 uppercase tracking-widest">
                    {equipped.style || "melee"}
                  </div>
                  <div className="font-numeric text-[10px] text-stone-500 leading-tight">
                    ATK +{equipped.atk} · STR +{equipped.str} · SPD {equipped.speed}t
                  </div>
                  <button onClick={doUnequip} className="btn-ghost-gold text-[10px] px-2 py-0.5 mt-1" data-testid="unequip-btn">
                    Unequip
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-stone-500 font-body italic text-xs text-center py-2" data-testid="equipped-empty">
                Fists only. Double-click a weapon in Inventory to equip.
              </div>
            )}
          </Card>
        </div>

        {/* Center column: active fight + monster picker */}
        <div className="lg:col-span-6 space-y-3">
          <Card
            title={activeMonster ? "Active Fight" : "Not In Combat"}
            testid="active-fight-card"
            right={activeMonster && (
              <button onClick={flee} className="btn-ghost-gold flex items-center gap-1 px-2 py-0.5 text-[10px]" data-testid="flee-btn">
                <StopCircle size={12} /> Withdraw
              </button>
            )}
          >
            {activeMonster ? (
              <div className="flex gap-4 items-start" data-testid="active-fight">
                <div className="flex-shrink-0 w-28 h-28 rounded panel-inset flex items-center justify-center overflow-hidden">
                  {monsterIcon(combat.monster_id) ? (
                    <img src={monsterIcon(combat.monster_id)} alt={activeMonster.name} className="w-full h-full object-cover" />
                  ) : (
                    <Skull size={40} className="text-red-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-xl heading-gilded leading-tight">{activeMonster.name}</h3>
                  <div className="text-stone-500 text-[10px] font-numeric uppercase tracking-widest">
                    CB {activeMonster.combat_level} · {content?.zones?.[activeMonster.zone]?.name}
                    {combat.style && <span className="ml-2 text-amber-500">Style: {combat.style}</span>}
                  </div>
                  <div className="mt-3">
                    <div className="text-[10px] font-numeric text-red-300 mb-1">
                      HP {combat.monster_hp} / {activeMonster.hp}
                    </div>
                    <div className="hp-bar-outer">
                      <div className="hp-bar-fill" style={{ width: `${monsterHpPct}%` }} data-testid="monster-hp-fill" />
                    </div>
                    <div className="mt-2 flex gap-4 text-xs font-body">
                      <div>Kills: <span className="font-numeric text-amber-300">{combat.kills || 0}</span></div>
                      <div className="text-stone-500">
                        {combat.respawn_in > 0 ? `Respawning in ${combat.respawn_in} ticks…` : "Engaged"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-stone-500 italic text-xs font-body">
                Pick a foe below to begin the tick loop.
              </div>
            )}
          </Card>

          <Card title="Choose Your Foe" testid="monster-picker">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[560px] overflow-y-auto scroll-thin pr-1">
              {monsterList.map((m) => (
                <article key={m.id} className="panel-inset p-2 flex gap-2" data-testid={`monster-card-${m.id}`}>
                  <div className="w-14 h-14 flex-shrink-0 rounded panel-inset overflow-hidden flex items-center justify-center">
                    {monsterIcon(m.id) ? (
                      <img src={monsterIcon(m.id)} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <Swords size={20} className="text-red-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-display text-sm leading-tight truncate">{m.name}</h4>
                      <span className="text-[10px] font-numeric text-red-400 shrink-0">CB {m.combat_level}</span>
                    </div>
                    <div className="text-[10px] text-stone-500 font-numeric mt-0.5">
                      HP {m.hp} · Max {m.max_hit} · +{m.xp_per_kill} XP
                    </div>
                    <div className="text-[10px] text-stone-500 mt-0.5 font-body truncate">
                      {m.drops_table.map((d) => itemName(d[0], items)).join(", ") || "—"}
                    </div>
                    <button
                      onClick={() => fight(m.id)}
                      className="btn-gold text-[10px] px-2 py-0.5 mt-1"
                      data-testid={`fight-btn-${m.id}`}
                    >
                      Fight
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </Card>
        </div>

        {/* Right column: Combat skills grid + gold */}
        <div className="lg:col-span-3 space-y-3">
          <Card title="Combat Skills" testid="combat-skills-grid">
            <div className="grid grid-cols-3 gap-1.5">
              {COMBAT_SKILLS.map((s) => (
                <SkillBadge
                  key={s}
                  skill={s}
                  lv={snap?.skills?.[s]?.level ?? 1}
                  forwardedRef={(el) => { if (el) badgeRefs.current[s] = el; }}
                />
              ))}
            </div>
          </Card>

          <Card title="Vitals" testid="vitals-card">
            <div className="flex items-center gap-2 mb-1">
              <Heart size={12} className="fill-red-500 text-red-500" />
              <div className="flex-1 hp-bar-outer">
                <div className="hp-bar-fill" style={{ width: `${snap?.max_hp ? (snap.current_hp / snap.max_hp) * 100 : 100}%` }} />
              </div>
              <span className="text-[10px] font-numeric text-red-300">
                {snap?.current_hp ?? 0}/{snap?.max_hp ?? 0}
              </span>
            </div>
            <dl className="text-xs font-body space-y-1 mt-2">
              <div className="flex justify-between">
                <dt className="text-stone-500">Gold</dt>
                <dd className="text-amber-300 font-numeric flex items-center gap-1">
                  <Coins size={11} />{snap?.gold?.toLocaleString?.() ?? 0}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500">Prayer</dt>
                <dd className="text-amber-300 font-numeric">{snap?.skills?.prayer?.level ?? 1}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500">Combat Style</dt>
                <dd className="text-amber-300">{snap?.combat_style || "melee"}</dd>
              </div>
            </dl>
          </Card>
        </div>

        {/* Full width: Primary combat stats */}
        <div className="lg:col-span-12">
          <Card title="Primary Combat Stats" testid="primary-combat-stats">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <StatRow label="Attack"     level={snap?.skills?.attack?.level ?? 1} />
              <StatRow label="Strength"   level={snap?.skills?.strength?.level ?? 1} />
              <StatRow label="Defence"    level={snap?.skills?.defence?.level ?? 1} />
              <StatRow label="Hitpoints"  level={snap?.skills?.hitpoints?.level ?? 10} />
              <StatRow label="Ranged"     level={snap?.skills?.ranged?.level ?? 1} />
              <StatRow label="Magic"      level={snap?.skills?.magic?.level ?? 1} />
              <StatRow label="Prayer"     level={snap?.skills?.prayer?.level ?? 1} />
              <StatRow label="Slayer"     level={snap?.skills?.slayer?.level ?? 1} />
            </div>
          </Card>
        </div>

        {/* Secondary stats */}
        <div className="lg:col-span-12">
          <Card
            title="Secondary Combat Stats"
            testid="secondary-combat-stats"
            right={
              <button onClick={() => setShowSecondary((v) => !v)} className="text-stone-500 hover:text-amber-400">
                <ChevronDown size={12} className={showSecondary ? "" : "-rotate-90"} />
              </button>
            }
          >
            {showSecondary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {secondary.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex items-start gap-3 py-2 border-b border-stone-800 last:border-none">
                      <div className="w-8 h-8 rounded-full bg-amber-900/30 border border-amber-700 flex items-center justify-center text-amber-400">
                        <Icon size={14} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-body text-stone-200">{s.label}</div>
                        <div className="text-[11px] text-stone-500 italic">{s.desc}</div>
                      </div>
                      <div className="font-numeric text-amber-300 text-sm">{s.value}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
